import { useQuery, useInfiniteQuery, useMutation, useTransport, createConnectQueryKey } from '@connectrpc/connect-query'
import { create } from '@bufbuild/protobuf'
import { keepPreviousData, useQueryClient } from '@tanstack/react-query'
import type { QueryClient } from '@tanstack/react-query'
import type { Transport } from '@connectrpc/connect'
import { useMemo } from 'react'

import {
  listContacts,
  createContact,
  getContact,
  updateContact,
  deleteContact,
  archiveContact,
  unarchiveContact,
  addContactEmail,
  removeContactEmail,
  setPrimaryContactEmail,
  getCompany,
  getInquiry,
  getEventDetail,
} from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import {
  ListContactsRequestSchema,
  GetContactRequestSchema,
  GetContactResponseSchema,
} from '@/gen/cortado/v1/cortado_pb'
import {
  ArchiveContactRequestSchema,
  UnarchiveContactRequestSchema,
} from '@/gen/cortado/v1/contacts_pb'
import {
  AddContactEmailRequestSchema,
  RemoveContactEmailRequestSchema,
  SetPrimaryContactEmailRequestSchema,
} from '@/gen/cortado/v1/cortado_pb'
import { organizationStore } from './organization.store'
import { useStore } from '@tanstack/react-store'

/**
 * Hook to fetch all contacts for an organization
 *
 * @param organizationId - Optional organization ID (defaults to selected org from store)
 * @param options - Optional extras; `enabled` gates the fetch on top of the org guard (default true)
 * @returns Query result with contacts array, loading state, and error
 */
export function useContacts(organizationId?: string, options?: { enabled?: boolean; includeArchived?: boolean }) {
  const selectedOrganizationId = useStore(organizationStore, (state) => state.selectedOrganizationId)
  const orgId = organizationId || selectedOrganizationId

  const request = useMemo(() => {
    return create(ListContactsRequestSchema, {
      organizationId: orgId || '',
      includeArchived: options?.includeArchived,
    })
  }, [orgId, options?.includeArchived])

  const query = useQuery(
    listContacts,
    request,
    {
      enabled: !!orgId && (options?.enabled ?? true),
      select: (data) => data?.contacts || [],
    }
  )

  return {
    contacts: query.data || [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  }
}

/** Page size for the paginated contacts list (contacts/index.tsx). */
const CONTACTS_PAGE_SIZE = 100

/**
 * Paginated variant of {@link useContacts} for the Contacts list page.
 *
 * Fetches contacts in offset-based pages of {@link CONTACTS_PAGE_SIZE} (ordered
 * alphabetically by last name server-side) and exposes `fetchNextPage`/`hasMore`
 * for a "Load more" affordance driven by the real `totalCount`. Consumers that
 * need the full set in one shot — the contact pickers and Cmd+K global search —
 * keep using {@link useContacts} (which sends no limit, so the backend returns
 * all contacts).
 */
export function useInfiniteContactsList(organizationId?: string, options?: { search?: string; includeArchived?: boolean }) {
  const selectedOrganizationId = useStore(organizationStore, (state) => state.selectedOrganizationId)
  const orgId = organizationId || selectedOrganizationId

  // Init shape (not a created message) so `offset` is a definite number, which
  // connect-query's useInfiniteQuery requires for the page-param field.
  const input = useMemo(() => ({
    organizationId: orgId || '',
    search: options?.search,
    includeArchived: options?.includeArchived,
    limit: CONTACTS_PAGE_SIZE,
    offset: 0,
  }), [orgId, options?.includeArchived, options?.search])

  const query = useInfiniteQuery(listContacts, input, {
    enabled: !!orgId,
    pageParamKey: 'offset',
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, page) => sum + page.contacts.length, 0)
      return loaded < lastPage.totalCount ? loaded : undefined
    },
    placeholderData: keepPreviousData,
  })

  const contacts = useMemo(
    () => query.data?.pages.flatMap((page) => page.contacts) ?? [],
    [query.data],
  )
  const totalCount = query.data?.pages.at(-1)?.totalCount ?? 0

  return {
    contacts,
    totalCount,
    hasMore: contacts.length < totalCount,
    fetchNextPage: query.fetchNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    isError: query.isError,
    error: query.error,
    hasLoaded: query.isSuccess || !!query.data,
  }
}

/**
 * Hook to create a new contact
 *
 * This hook:
 * - Provides mutation function to create contact
 * - Updates cache with new contact
 * - Invalidates list queries to show new contact
 *
 * @returns Mutation result with createContact function and loading state
 */
export function useCreateContact() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(
    createContact,
    {
      onSuccess: (response) => {
        const newContact = response.contact

        if (!newContact) return

        // 1. Update individual contact cache (getContact)
        const queryKey = createConnectQueryKey({
          schema: getContact,
          transport,
          input: { id: newContact.id },
          cardinality: 'finite',
        })

        // Create a proper GetContactResponse message
        queryClient.setQueryData(
          queryKey,
          create(GetContactResponseSchema, { contact: newContact })
        )

        // 2. Invalidate list queries (we can't know all possible filter combinations)
        queryClient.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listContacts,
            transport,
            cardinality: undefined,
          }),
        })
      },
    }
  )

  return {
    mutation,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

/**
 * Hook to get a single contact by ID
 *
 * @param contactId - The contact ID to fetch
 * @returns Query result with contact, loading state, and error
 */
export function useContact(contactId?: string) {
  const query = useQuery(
    getContact,
    create(GetContactRequestSchema, {
      id: contactId ?? '',
    }),
    {
      enabled: !!contactId,
      select: (data) => data?.contact,
    },
  )

  return {
    contact: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  }
}

/**
 * Hook to update an existing contact
 *
 * This hook:
 * - Provides mutation function to update contact
 * - Updates individual contact cache with updated data
 * - Invalidates list queries to reflect changes
 *
 * @returns Mutation result with mutation function and loading state
 */
export function useUpdateContact() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(
    updateContact,
    {
      onSuccess: (response) => {
        const updatedContact = response.contact

        if (!updatedContact) return

        // 1. Update individual contact cache (getContact)
        const queryKey = createConnectQueryKey({
          schema: getContact,
          transport,
          input: { id: updatedContact.id },
          cardinality: 'finite',
        })

        // Create a proper GetContactResponse message
        queryClient.setQueryData(
          queryKey,
          create(GetContactResponseSchema, { contact: updatedContact })
        )

        // 2. Invalidate list queries (we can't know all possible filter combinations)
        queryClient.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listContacts,
            transport,
            cardinality: undefined,
          }),
        })
      },
    }
  )

  return {
    mutation,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

/**
 * Hook to delete a contact
 *
 * This hook:
 * - Provides mutation function to delete contact
 * - Removes contact from individual cache
 * - Invalidates list queries to remove deleted contact
 *
 * @returns Mutation result with mutation function and loading state
 */
export function useDeleteContact() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(
    deleteContact,
    {
      onSuccess: (_, variables) => {
        const contactId = variables.id

        if (!contactId) return

        // 1. Remove individual contact cache (getContact)
        const queryKey = createConnectQueryKey({
          schema: getContact,
          transport,
          input: { id: contactId },
          cardinality: 'finite',
        })

        queryClient.removeQueries({ queryKey })

        // 2. Invalidate list queries (we can't know all possible filter combinations)
        queryClient.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listContacts,
            transport,
            cardinality: undefined,
          }),
        })
      },
    }
  )

  return {
    mutation,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

function invalidateContactArchiveCaches(
  queryClient: QueryClient,
  transport: Transport,
  contactId: string,
) {
  void queryClient.invalidateQueries({
    queryKey: createConnectQueryKey({
      schema: getContact,
      transport,
      input: { id: contactId },
      cardinality: 'finite',
    }),
  })

  void queryClient.invalidateQueries({
    queryKey: createConnectQueryKey({ schema: listContacts, transport, cardinality: undefined }),
    refetchType: 'all',
  })
  void queryClient.invalidateQueries({
    queryKey: createConnectQueryKey({ schema: getCompany, transport, cardinality: undefined }),
    refetchType: 'all',
  })
  void queryClient.invalidateQueries({
    queryKey: createConnectQueryKey({ schema: getInquiry, transport, cardinality: undefined }),
    refetchType: 'all',
  })
  void queryClient.invalidateQueries({
    queryKey: createConnectQueryKey({ schema: getEventDetail, transport, cardinality: undefined }),
    refetchType: 'all',
  })
}

/** Archives a contact without removing it from existing leads, events, or companies. */
export function useArchiveContact() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(archiveContact, {
    onSuccess: (_response, variables) => {
      if (variables.id) invalidateContactArchiveCaches(queryClient, transport, variables.id)
    },
  })

  return {
    archive: (contactId: string) => mutation.mutateAsync(
      create(ArchiveContactRequestSchema, { id: contactId })
    ),
    mutation,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

/** Restores a previously archived contact. */
export function useUnarchiveContact() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(unarchiveContact, {
    onSuccess: (_response, variables) => {
      if (variables.id) invalidateContactArchiveCaches(queryClient, transport, variables.id)
    },
  })

  return {
    unarchive: (contactId: string) => mutation.mutateAsync(
      create(UnarchiveContactRequestSchema, { id: contactId })
    ),
    mutation,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

function invalidateContactEmailCaches(
  queryClient: QueryClient,
  transport: Transport,
  contactId: string,
) {
  // Refetch rather than write the response: GetContact also carries the
  // contact's companies, which the address RPCs do not return.
  void queryClient.invalidateQueries({
    queryKey: createConnectQueryKey({
      schema: getContact,
      transport,
      input: { id: contactId },
      cardinality: 'finite',
    }),
  })
  void queryClient.invalidateQueries({
    queryKey: createConnectQueryKey({ schema: listContacts, transport, cardinality: undefined }),
  })
}

/** Adds, removes and promotes a contact's email addresses. */
export function useContactEmails(contactId: string) {
  const queryClient = useQueryClient()
  const transport = useTransport()
  const onSuccess = () => invalidateContactEmailCaches(queryClient, transport, contactId)

  const add = useMutation(addContactEmail, { onSuccess })
  const remove = useMutation(removeContactEmail, { onSuccess })
  const setPrimary = useMutation(setPrimaryContactEmail, { onSuccess })

  return {
    addEmail: (email: string, label?: string) =>
      add.mutateAsync(create(AddContactEmailRequestSchema, { contactId, email, label })),
    removeEmail: (contactEmailId: string) =>
      remove.mutateAsync(create(RemoveContactEmailRequestSchema, { contactId, contactEmailId })),
    makePrimary: (contactEmailId: string) =>
      setPrimary.mutateAsync(create(SetPrimaryContactEmailRequestSchema, { contactId, contactEmailId })),
    isAdding: add.isPending,
    isBusy: add.isPending || remove.isPending || setPrimary.isPending,
  }
}
